import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Box, Stack } from '@mui/material';
import { useEvent } from '../context/useEvent';
import { EventForm } from '../components/EventForm/EventForm';
import { Button } from '../../../shared/components/ui/Button';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/hooks/useToast';

import { eventValidationSchema } from '../validations/eventValidationSchema';
import { eventInitialValues } from '../constants/tenantConstants';

import { useState } from 'react';
import { souvenirService } from '../../../shared/services/souvenirService/souvenirService';
import { formatDateInputEvent } from '../../../shared/utils/formatDateInput';
import { normalizeHtml } from '../../../utils/normalizeHtml';

export default function EventFormPage() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [organizerName, setOrganizerName] = useState('');
  const { showToast } = useToast();

  const {
    eventData,
    isDataLoading,
    createEvent,
    updateEvent,
    error,
    setError,
    fetchEvents
  } = useEvent();

  async function handleSubmit(formData: any) {
    const cleanData = {
      ...formData,
      overnight: formData.overnight !== undefined ? formData.overnight : null,
      description: normalizeHtml(formData.description || ''),
      observation: formData.observation ? normalizeHtml(formData.observation) : null,
      image: formData.image || null,
      video_url: formData.video_url || null,
      rules_for_children: formData.rules_for_children ? normalizeHtml(formData.rules_for_children) : null,
      own_transport: formData.own_transport !== undefined ? formData.own_transport : null,
      valor_ingresso_inteiro: Number(formData.valor_ingresso_inteiro),
      valor_ingresso_meia: Number(formData.valor_ingresso_meia),
      valor_ingresso_estudante: Number(formData.valor_ingresso_estudante),
      valor_ingresso_crianca: Number(formData.valor_ingresso_crianca),
      organizer_id: formData.organizer_id || null,
      secondary_organizer_ids: formData.secondary_organizer_ids && Array.isArray(formData.secondary_organizer_ids) && formData.secondary_organizer_ids.length > 0
        ? formData.secondary_organizer_ids
        : undefined,
      dynamicFields: formData.dynamicFields?.length ? formData.dynamicFields : [],
      souvenirs: undefined,
      theme_customization: formData.theme_customization !== undefined ? formData.theme_customization : null,
      payment_pix: formData.payment_pix ?? true,
      payment_card: formData.payment_card ?? true,
      payment_boleto: formData.payment_boleto ?? true,
      boleto_interest: formData.boleto_interest ?? 0,
      boleto_fine: formData.boleto_fine ?? 0,
      card_fee: formData.card_fee ?? 0,
      card_fee_percent: formData.card_fee_percent ?? 0,
      gtm_container_id: formData.gtm_container_id?.trim()
        ? formData.gtm_container_id.trim().toUpperCase()
        : null,
    };
    const { participants, organizer, organizer_name, souvenirs, ...body } = cleanData;
    if (formData.image instanceof File) {
      delete body.image;
    }

    let submitBody: any = body;
    let config = {};

    if (body.theme_customization === null || body.theme_customization === undefined) {
      body.theme_customization = null;
    }

    if (formData.image instanceof File) {
      submitBody = new FormData();
      Object.entries(body).forEach(([key, value]) => {
        if (key === 'theme_customization') {
          submitBody.append(key, value !== null && value !== undefined ? JSON.stringify(value) : 'null');
        }
        else if (value !== undefined && value !== null) {
          if (key === 'dynamicFields') {
            submitBody.append(key, JSON.stringify(value));
          }
          else if (key === 'secondary_organizer_ids') {
            submitBody.append(key, JSON.stringify(value));
          }
          else if (key === 'overnight' || key === 'own_transport' || key === 'hasTickets' || key === 'requireCheckIn' || key === 'payment_pix' || key === 'payment_card' || key === 'payment_boleto' || key === 'group_registration_enabled') {
            submitBody.append(key, value === true ? 'true' : 'false');
          }

          else if (
            key === 'valor_ingresso_inteiro' ||
            key === 'valor_ingresso_meia' ||
            key === 'valor_ingresso_estudante' ||
            key === 'valor_ingresso_crianca' ||
            key === 'boleto_interest' ||
            key === 'boleto_fine' ||
            key === 'card_fee' ||
            key === 'card_fee_percent'
          ) {
            submitBody.append(key, String(Number(value)));
          }
          else {
            submitBody.append(key, value);
          }
        }
      });
      submitBody.append('image', formData.image);
      config = { headers: { 'Content-Type': 'multipart/form-data' } };
    } else {
      if (submitBody.theme_customization === undefined) {
        submitBody.theme_customization = null;
      }
    }

    try {
      let eventId = id;

      if (isEdit && id) {
        await updateEvent(id, submitBody, config);
        showToast('Evento atualizado com sucesso!', 'success');
        fetchEvents();
      } else {

        const createdEventData = await createEvent(submitBody, config);
        eventId = createdEventData?.id;
        showToast('Evento criado com sucesso!', 'success');
        fetchEvents();
      }

      if (!eventId) {
        showToast('Erro: ID do evento não encontrado', 'error');
        return;
      }

      if (isEdit && eventData?.souvenirs) {
        const originalIds = eventData.souvenirs.map((s: any) => s.id);
        const currentIds = (formData.souvenirs || []).map((s: any) => s.id).filter(Boolean);
        const removedIds = originalIds.filter((id: string) => !currentIds.includes(id));
        for (const removedId of removedIds) {
          await souvenirService.remove(removedId);
        }
      }

      if (formData.souvenirs && formData.souvenirs.length) {
        for (const souvenir of formData.souvenirs) {
          if (souvenir.id) {
            await souvenirService.update(souvenir.id, {
              title: souvenir.title,
              description: souvenir.description,
              image: souvenir.image,
            });
          } else {
            await souvenirService.create({
              title: souvenir.title,
              description: souvenir.description,
              image: souvenir.image,
              event_id: eventId,
            });
          }
        }
      }

      fetchEvents();
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      navigate(isEdit ? `/events/${eventId}` : '/events');
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Erro ao salvar evento', 'error');
      console.error('Erro ao salvar evento/souvenirs:', error);
    }
  }
  function handleCancel() {
    navigate(-1);
  }

  if (isEdit && isDataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        Carregando...
      </Box>
    );
  }

  const formInitialValues = isEdit && eventData
    ? {
      ...eventInitialValues,
      ...eventData,
      description: eventData.description ? normalizeHtml(eventData.description) : '',
      observation: eventData.observation ? normalizeHtml(eventData.observation) : null,
      rules_for_children: eventData.rules_for_children ? normalizeHtml(eventData.rules_for_children) : null,
      organizer_name: eventData?.organizer?.name || '',
      secondary_organizer_ids: eventData.secondary_organizer_ids || [],
      souvenirs: eventData.souvenirs || [],
      dynamicFields: eventData.dynamicFields
        ? eventData.dynamicFields.map((field: any, index: number) => {
          // Mapear valores antigos ou inválidos para valores válidos
          const validTypes = ['text', 'yes_no', 'date', 'email', 'phone', 'select'];
          let fieldType = field.type || field.field_type || 'text';

          // Se o tipo não for válido, mapear para 'text'
          if (!validTypes.includes(fieldType)) {
            // Mapear valores antigos comuns
            if (fieldType === 'string' || fieldType === 'number' || fieldType === 'boolean') {
              fieldType = 'text';
            } else {
              fieldType = 'text'; // Default para 'text' se não reconhecido
            }
          }

          return {
            id: field?.id,
            label: field.label,
            placeholder: field.placeholder || '',
            type: fieldType,
            required: field.required,
            options: field.options || [],
            optionsString:
              (fieldType === 'select' && field.options)
                ? field.options.join(',')
                : '',
            _order: index,
          };
        })
        : [],
      status: eventData.status || 'open',
      registration_deadline: formatDateInputEvent(eventData?.registration_deadline),
      payment_pix: eventData.payment_pix ?? true,
      payment_card: eventData.payment_card ?? true,
      show_organizer_email: eventData.show_organizer_email !== undefined ? eventData.show_organizer_email : true,
      show_organizer_phone: eventData.show_organizer_phone !== undefined ? eventData.show_organizer_phone : true,
      payment_boleto: eventData.payment_boleto ?? true,
      boleto_interest: eventData.boleto_interest ?? 0,
      boleto_fine: eventData.boleto_fine ?? 0,
      card_fee: eventData.card_fee ?? 0,
      card_fee_percent: eventData.card_fee_percent ?? 0,
      group_registration_enabled: eventData.group_registration_enabled ?? false,
    }
    : eventInitialValues;

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {error}
        </Box>
      )}

      <Formik
        initialValues={formInitialValues}
        validationSchema={eventValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={false}
      >
        {(props) => {
          return (
            <Form>
              <EventForm
                mode={isEdit ? 'edit' : 'create'}

              />

              <Box sx={{ mt: 3, p: 2 }}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={props.isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    loading={props.isSubmitting}
                    disabled={props.isSubmitting}

                  >
                    {isEdit ? 'Salvar' : 'Criar'}
                  </Button>
                </Stack>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Box>
  );
}